import CalendarIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/Calendar.js";
import ExpandLeftIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandLeft.js";
import ExpandRightIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandRight.js";
import { applyTokens } from "../../../utilities/applyTokens.js";
import { createFocusStyle } from "../utils/createFocusStyle.js";
const datePicker = (tokens) => {
    const { container, stepper, monthView, monthsView, yearsView } = tokens.component.calendar;
    const { small, tertiary } = tokens.component.button;
    const { borderWidth, color } = tokens.semantic;
    const { spacing } = tokens.core;
    const { day } = monthView;
    const { first, last } = day.range;
    const focusSafeAreaOffset = spacing["0_5"].value;
    return {
        MuiDatePicker: {
            defaultProps: {
                components: {
                    OpenPickerIcon: CalendarIcon,
                    LeftArrowIcon: ExpandLeftIcon,
                    RightArrowIcon: ExpandRightIcon,
                },
            },
        },
        MuiCalendarOrClockPicker: {
            styleOverrides: {
                root: {
                    "& > div": {
                        // We need to remove the built-in maxHeight, so that we can add
                        // more spacing to the container
                        maxHeight: "unset !important",
                    },
                    "& .AtlasDatePickerToolbar-year + div .MuiPickersCalendarHeader-root": {
                        display: "none",
                    },
                },
            },
        },
        MuiYearPicker: {
            styleOverrides: {
                root: Object.assign(Object.assign({ width: "auto", padding: 0, justifyContent: "space-between", flexBasis: "auto", maxHeight: `calc(4 * ${day.medium.height.value} + 3 * ${yearsView.gap.value} + 2 * ${focusSafeAreaOffset})` }, applyTokens(yearsView)), { "& .PrivatePickersYear-root": {
                        flexBasis: "auto",
                    }, "& .PrivatePickersYear-yearButton": Object.assign(Object.assign(Object.assign(Object.assign({ margin: 0, padding: 0 }, applyTokens(day.medium)), applyTokens(day.unselected.default)), applyTokens(yearsView.year.medium)), { "&:hover": applyTokens(day.unselected.hover), "&:focus": day.unselected.hover, "&.Mui-selected": Object.assign(Object.assign({}, applyTokens(day.selected.default)), { "&:hover": applyTokens(day.selected.hover), "&:focus": applyTokens(day.selected.hover) }) }) }),
            },
        },
        MuiMonthPicker: {
            styleOverrides: {
                root: Object.assign(Object.assign({ width: "auto", justifyContent: "space-between", display: "grid", gridTemplateColumns: "repeat(3, 1fr)" }, applyTokens(monthsView)), { "& .PrivatePickersMonth-root": Object.assign(Object.assign(Object.assign(Object.assign({ margin: 0, padding: 0, flex: 0 }, applyTokens(day.medium)), applyTokens(day.unselected.default)), applyTokens(monthsView.month.medium)), { "&:hover": applyTokens(day.unselected.hover), "&:focus": day.unselected.hover, "&.Mui-selected": Object.assign(Object.assign({}, applyTokens(day.selected.default)), { "&:hover": applyTokens(day.selected.hover), "&:focus": applyTokens(day.selected.hover) }) }) }),
            },
        },
        MuiDayPicker: {
            styleOverrides: {
                header: {
                    justifyContent: "space-between",
                    paddingBottom: monthView.gap.value,
                    gap: monthView.weekHeader.gap.value,
                },
                monthContainer: {
                    display: "flex",
                    flexDirection: "column",
                    gap: monthView.gap.value,
                    padding: focusSafeAreaOffset,
                },
                slideTransition: {
                    margin: `-${focusSafeAreaOffset}`,
                    "&:has(.MuiDayPicker-monthContainer .MuiDayPicker-weekContainer:nth-of-type(4))": {
                        "--num-of-weeks": "4",
                    },
                    "&:has(.MuiDayPicker-monthContainer .MuiDayPicker-weekContainer:nth-of-type(5))": {
                        "--num-of-weeks": "5",
                    },
                    "&:has(.MuiDayPicker-monthContainer .MuiDayPicker-weekContainer:nth-of-type(6))": {
                        "--num-of-weeks": "6",
                    },
                    minHeight: `calc((var(--num-of-weeks) * ${day.medium.height.value}) + ((var(--num-of-weeks) - 1) * ${monthView.gap.value}) + (2 * ${focusSafeAreaOffset}))`,
                    transition: "min-height 0.2s",
                    overflowY: "hidden",
                },
                weekContainer: {
                    margin: 0,
                    justifyContent: "space-between",
                },
                weekDayLabel: {
                    "&&": Object.assign({ margin: 0 }, applyTokens(monthView.weekHeader.day)),
                },
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    margin: 0,
                    "&.Mui-disabled": applyTokens(day.unselected.disabled),
                },
                dayWithMargin: Object.assign(Object.assign(Object.assign({}, applyTokens(day.medium)), applyTokens(day.unselected.default)), { "&:not(.MuiPickersDay-today)": {
                        "&:hover": day.unselected.hover,
                        "&:focus": day.unselected.hover,
                    }, "&.Mui-selected:not(.MuiPickersDay-today)": Object.assign(Object.assign({}, applyTokens(day.selected.default)), { "&:hover": applyTokens(day.selected.hover), "&:focus": applyTokens(day.selected.hover) }) }),
                today: {
                    "&&": Object.assign(Object.assign({}, applyTokens(day.current.default)), { "&:hover": applyTokens(day.current.hover), "&:focus": day.current.hover, "&.Mui-selected": Object.assign(Object.assign({}, applyTokens(day.selected.default)), { "&:hover": applyTokens(day.selected.hover), "&:focus": applyTokens(day.selected.hover) }) }),
                },
            },
        },
        MuiPickersFadeTransitionGroup: {
            styleOverrides: {
                root: {
                    "& > div + div": {
                        height: "0",
                        overflow: "hidden",
                    },
                },
            },
        },
        MuiCalendarPicker: {
            styleOverrides: {
                root: {
                    maxHeight: "unset",
                    padding: container.padding.value,
                    width: "320px",
                },
            },
        },
        MuiPickersCalendarHeader: {
            styleOverrides: {
                label: Object.assign(Object.assign(Object.assign({ marginRight: 0 }, applyTokens(small)), applyTokens(tertiary.default)), { "&:hover": applyTokens(tertiary.hover), "&:active": applyTokens(tertiary.active), "&:focus": createFocusStyle(tokens) }),
                labelContainer: {
                    marginRight: "unset",
                    overflow: "visible",
                },
                root: {
                    position: "relative",
                    padding: 0,
                    margin: 0,
                    marginBottom: container.gap.value,
                    justifyContent: "center",
                },
                switchViewButton: {
                    display: "none",
                },
            },
        },
        MuiPickersArrowSwitcher: {
            styleOverrides: {
                button: {
                    pointerEvents: "auto",
                    color: stepper.button.color.value,
                },
                root: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pointerEvents: "none",
                    position: "absolute",
                    inset: 0,
                },
            },
        },
        /* Range picker */
        MuiDateRangePickerViewDesktop: {
            styleOverrides: {
                root: {
                    padding: spacing[3].value,
                    "& .MuiPickersArrowSwitcher-root": {
                        position: "relative",
                        padding: spacing[0].value,
                        "& .MuiTypography-subtitle1": applyTokens(small),
                    },
                    "& .PrivatePickersSlideTransition-root": {
                        // The SlideTransition component needs to have a fixed size,
                        // so we will calculate it based on the day size, gap between weeks, and the focus safe area offset.
                        minWidth: `calc((7 * ${day.medium.width.value}) + (6 * ${monthView.weeks.gap.value}) + (2 * ${focusSafeAreaOffset}))`,
                        width: `calc((7 * ${day.medium.width.value}) + (6 * ${monthView.weeks.gap.value}) + (2 * ${focusSafeAreaOffset}))`,
                        minHeight: `calc((6 * ${day.medium.height.value}) + (5 * ${monthView.gap.value}) + (2 * ${focusSafeAreaOffset}))`,
                    },
                    "& .MuiDayPicker-monthContainer": {
                        gap: monthView.gap.value,
                    },
                },
                container: {
                    display: "flex",
                    flexDirection: "column",
                    // Adding spacing between the two calendars
                    gap: container.gap.value,
                    "&:not(:last-of-type)": {
                        paddingRight: spacing["3"].value,
                        borderWidth: borderWidth.thin.value,
                        borderColor: color.ui.divider.default.value,
                    },
                    "&:last-of-type": {
                        paddingLeft: spacing["3"].value,
                    },
                    "& .MuiDayPicker-weekContainer": {
                        gap: monthView.weeks.gap.value,
                    },
                },
            },
        },
        MuiDateRangePickerDay: {
            styleOverrides: {
                root: {
                    display: "flex",
                    width: day.medium.width.value,
                    height: day.medium.height.value,
                    paddingRight: spacing[0].value,
                    "&:last-of-type .MuiDateRangePickerDay-rangeIntervalDayPreview::after": {
                        right: spacing[0].value, // On the last day of the week, the custom preview border doesn't need to overreach
                    },
                },
                day: Object.assign(Object.assign(Object.assign({}, applyTokens(day.medium)), applyTokens(day.unselected.default)), { backgroundColor: "transparent", transform: "none", "&:not(.MuiPickersDay-today)": {
                        "&:hover": day.unselected.hover,
                        "&:focus": day.unselected.hover,
                    }, "&.Mui-selected:not(.MuiPickersDay-today)": Object.assign(Object.assign({}, applyTokens(day.selected.default)), { "&:hover": applyTokens(day.selected.hover), "&:focus": applyTokens(day.selected.hover) }) }),
                /* Selected range highlight */
                rangeIntervalDayHighlight: {
                    marginInline: spacing[0].value,
                    backgroundColor: "transparent",
                    position: "relative",
                    // And add a custom background color with a pseudo-element
                    "&::before": {
                        content: "''",
                        position: "absolute",
                        inset: spacing[0].value,
                        right: `-${monthView.weeks.gap.value}`,
                        backgroundColor: day.range.backgroundColor.value,
                        pointerEvents: "none",
                    },
                    "&:first-of-type::before": applyTokens(first),
                    "&:last-of-type::before": Object.assign(Object.assign({}, applyTokens(last)), { right: spacing[0].value }),
                },
                rangeIntervalDayHighlightStart: {
                    marginInline: spacing[0].value,
                    "&::before": applyTokens(first),
                },
                rangeIntervalDayHighlightEnd: {
                    marginInline: 0,
                    "&::before": Object.assign(Object.assign({}, applyTokens(last)), { right: spacing[0].value }),
                },
                /* Range preview */
                rangeIntervalPreview: {
                    position: "relative",
                    display: "flex",
                    // We're turning off the built-in border, so that it doesn't cause a layout shift
                    borderWidth: spacing[0].value,
                    width: day.medium.width.value,
                    height: day.medium.height.value,
                },
                rangeIntervalDayPreview: {
                    // And we're adding a custom border to the preview
                    "&::after": {
                        content: "''",
                        position: "absolute",
                        inset: spacing[0].value,
                        // The custom border needs to reach the edge of the next day,
                        // plus one pixel to look nicer
                        right: `calc(-1 * (${monthView.weeks.gap.value} + 1px))`,
                        borderTopWidth: borderWidth.thin.value,
                        borderBottomWidth: borderWidth.thin.value,
                        borderLeftWidth: spacing[0].value,
                        borderRightWidth: spacing[0].value,
                        borderStyle: "dotted",
                        borderColor: color.ui.divider.default.value,
                        pointerEvents: "none",
                    },
                },
                rangeIntervalDayPreviewStart: {
                    marginInline: spacing[0].value,
                    "&::after": Object.assign(Object.assign({}, applyTokens(first)), { borderLeftWidth: borderWidth.thin.value, borderRightWidth: spacing[0].value }),
                },
                rangeIntervalDayPreviewEnd: {
                    marginInline: spacing[0].value,
                    "&::after": Object.assign(Object.assign({}, applyTokens(last)), { borderLeftWidth: spacing[0].value, borderRightWidth: borderWidth.thin.value, right: spacing[0].value }),
                },
            },
        },
    };
};
export default datePicker;
//# sourceMappingURL=datePicker.js.map